import React, { useEffect, useRef } from 'react';

interface EnhancedGifPlayerProps {
  src: string;
  alt: string;
  className?: string;
}

export default function EnhancedGifPlayer({ src, alt, className = '' }: EnhancedGifPlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const currentImageRef = useRef<HTMLImageElement | null>(null);
  const nextImageRef = useRef<HTMLImageElement | null>(null);
  const isVisibleRef = useRef(false);
  const intervalRef = useRef<number>();
  const frameRequestRef = useRef<number>();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const createImage = () => {
      const img = new Image();
      img.src = src;
      img.alt = alt;
      img.className = `${className} absolute inset-0 w-full h-full object-contain will-change-transform`;
      img.loading = 'lazy';
      img.decoding = 'async';
      return img;
    };

    const swapImages = () => {
      if (!isVisibleRef.current || !container) return;

      if (frameRequestRef.current) {
        cancelAnimationFrame(frameRequestRef.current);
      }

      frameRequestRef.current = requestAnimationFrame(() => {
        // Create new image
        const newImage = createImage();
        newImage.style.opacity = '0';
        newImage.style.transition = 'opacity 300ms ease-out';
        
        // Add to container
        container.appendChild(newImage);
        
        // Force reflow
        newImage.offsetHeight;
        
        // Fade in new image
        newImage.style.opacity = '1';
        
        // Update references
        if (nextImageRef.current) {
          currentImageRef.current = nextImageRef.current;
        }
        nextImageRef.current = newImage;
        
        // Remove old image after transition
        if (currentImageRef.current) {
          setTimeout(() => {
            currentImageRef.current?.remove();
          }, 300);
        }
      });
    };

    const startAnimation = () => {
      isVisibleRef.current = true;
      swapImages();
      intervalRef.current = window.setInterval(swapImages, 2800);
    };

    const stopAnimation = () => {
      isVisibleRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
      if (frameRequestRef.current) {
        cancelAnimationFrame(frameRequestRef.current);
        frameRequestRef.current = undefined;
      }
    };

    // Create intersection observer with larger rootMargin for better preloading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startAnimation();
          } else {
            stopAnimation();
          }
        });
      },
      { 
        threshold: 0.1,
        rootMargin: '100px'
      }
    );

    observer.observe(container);

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAnimation();
      } else if (container.offsetParent !== null) {
        startAnimation();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Preload image
    const preloadImage = new Image();
    preloadImage.src = src;

    // Initial setup
    const initialImage = createImage();
    container.appendChild(initialImage);
    nextImageRef.current = initialImage;

    // Start if initially visible
    if (container.offsetParent !== null && !document.hidden) {
      startAnimation();
    }

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopAnimation();
      container.innerHTML = '';
    };
  }, [src, alt, className]);

  return (
    <div 
      ref={containerRef} 
      className="relative w-full h-full"
      style={{
        WebkitBackfaceVisibility: 'hidden',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        WebkitTransform: 'translate3d(0,0,0)',
        willChange: 'transform',
      }}
    />
  );
}